import { datadogLogs } from '@datadog/browser-logs'
import { PayloadAction } from '@reduxjs/toolkit'
import { diff } from 'json-diff-ts'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import { v4 as uuid } from 'uuid'

import { Coordinate } from '../../../models/activeDrawable'
import { Project } from '../../../models/project'
import { DRAWABLE_TYPES } from '../../../shared/constants/drawable-types'
import IndexableObject from '../../../shared/constants/general-enums/indexableObject'
import { getSelectedMaterial, setLatestAnalyticsAction } from '../../slices/2D'
import {
    selectAnalyticsActionId,
    setAnalyticsId,
    setEditInitialCoordinates,
    setSelectionModificationInitialSettings,
} from '../../slices/analytics'
import { selectProject } from '../../slices/project'
import { AnalyticsActionEdgeType, AnalyticsActionType, AnalyticsEvent, AnalyticsUserAction } from '../../types'

export function* getAnalyticsId(startOrStop: AnalyticsActionEdgeType, actionType: AnalyticsActionType) {
    if (startOrStop === AnalyticsActionEdgeType.START) {
        const newId = yield call<() => string>(uuid)

        return newId
    }

    const analyticsId: string | null = yield select(selectAnalyticsActionId, actionType)

    return analyticsId
}

export function* getAnalyticsCoordinates(
    startOrStop: AnalyticsActionEdgeType,
    actionType: AnalyticsActionType,
    newCoordinates: Coordinate[] | undefined
) {
    if (actionType === AnalyticsActionType.EDIT && startOrStop === AnalyticsActionEdgeType.STOP) {
        const coordinates: Coordinate[] | null = yield select((state) => state.IMUP.analytics.editInitialCoordinates)

        return { initial: coordinates, final: newCoordinates ?? [] }
    }

    return null
}

export function* getModificationSettingsDelta(
    startOrStop: AnalyticsActionEdgeType,
    actionType: AnalyticsActionType,
    settings?: Record<string, any>
) {
    if (actionType === AnalyticsActionType.SELECTION_MODIFICATION && startOrStop === AnalyticsActionEdgeType.STOP) {
        const initialSettings: IndexableObject | null = yield select(
            (state) => state.IMUP.analytics.selectionModificationInitialSettings
        )

        return initialSettings && settings
            ? diff(initialSettings, settings, {
                  treatTypeChangeAsReplace: false,
                  keysToSkip: ['quantity', 'linear_total'],
              })
            : null
    }

    return null
}

export function* handleLoggingAnalyticsAction(payload: PayloadAction<AnalyticsUserAction>) {
    const project: Project | null = yield select(selectProject)

    if (!project) return

    try {
        const { startOrStop, actionType, coordinates, settings } = payload.payload

        if (!project.portal_number) return

        const lastActiveGroupType: string | null = yield select((state) => state.IMUP.geometry.lastActiveGroupType)
        const selectedMaterialType: DRAWABLE_TYPES | null = yield select(getSelectedMaterial)

        const type =
            selectedMaterialType && selectedMaterialType !== lastActiveGroupType
                ? selectedMaterialType
                : lastActiveGroupType

        if (!type) return

        const analyticsId: string | null = yield call(getAnalyticsId, startOrStop, actionType)

        if (startOrStop === AnalyticsActionEdgeType.START) {
            if (analyticsId) {
                yield put(setAnalyticsId({ actionType, id: analyticsId }))
            }

            if (actionType === AnalyticsActionType.EDIT && coordinates) {
                yield put(setEditInitialCoordinates(coordinates))
            }

            if (actionType === AnalyticsActionType.SELECTION_MODIFICATION) {
                yield put(setSelectionModificationInitialSettings(settings || null))
            }
        }

        const analyticsData: AnalyticsEvent = {
            portalNumber: project.portal_number,
            actionType,
            startOrStop,
            timeStamp: new Date().toISOString(),
            materialType: type,
            delta: yield call(getModificationSettingsDelta, startOrStop, actionType, settings),
            coordinates: yield call(getAnalyticsCoordinates, startOrStop, actionType, coordinates),
            id: analyticsId,
        }

        yield call(datadogLogs.logger.info.bind(datadogLogs.logger), 'estimation-action', analyticsData)
    } catch (err) {
        console.error(err)
    }
}

export function* watchForAnalyticsAction() {
    yield takeEvery(setLatestAnalyticsAction.type, handleLoggingAnalyticsAction)
}
